import { useState } from "react";
import { toast } from "sonner";
import { Percent, BrainCircuit, ShieldCheck, Clock3, PartyPopper, Flame, Users } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { DetailsForm } from "../components/assessment/DetailsForm";
import { QuizRunner } from "../components/assessment/QuizRunner";
import { ResultCard } from "../components/assessment/ResultCard";
import { LaunchResultCard, LAUNCH_MESSAGE } from "../components/assessment/LaunchResultCard";
import { useDiscountMeta } from "../components/DiscountCTA";
import { checkAssessmentMobile, startAssessment, submitAssessment, claimLaunchOffer } from "../lib/api";

const TIERS = [
  ["0–4", "0%"], ["5–6", "5%"], ["7", "10%"], ["8", "15%"], ["9", "20%"], ["10–12", "25%"], ["13–15", "30%"],
];

const errDetail = (err) => err?.response?.data?.detail;
const HERO_IMG = "https://images.pexels.com/photos/5212345/pexels-photo-5212345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920";

const InfoCard = ({ Icon, title, desc }) => (
  <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5">
    <div className="w-11 h-11 rounded-xl bg-[#00B37E]/10 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-[#00B37E]" /></div>
    <div>
      <div className="font-heading font-bold text-[#0A192F]">{title}</div>
      <div className="text-sm text-[#64748B] mt-0.5">{desc}</div>
    </div>
  </div>
);

export default function DiscountAssessment() {
  const meta = useDiscountMeta();
  const launch = meta.mode === "launch";
  const [step, setStep] = useState("details"); // details | quiz | result
  const [attempt, setAttempt] = useState(null);
  const [result, setResult] = useState(null);
  const [previous, setPrevious] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = (form) => {
    if (!form.name || !form.age || !form.mobile || !form.email || !form.education) { toast.error("Please fill all the fields."); return false; }
    return true;
  };
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const showExisting = (res) => { setResult(res); setPrevious(true); setStep("result"); };

  const onMobileBlur = async (mobile) => {
    if (launch || mobile.replace(/\D/g, "").length < 10) return;
    try {
      const data = await checkAssessmentMobile(mobile);
      if (data.blocked) { toast.warning("This mobile number already has an active discount."); showExisting(data.result); }
    } catch { /* silent */ }
  };

  const onClaim = async (form) => {
    if (!validate(form)) return;
    setLoading(true);
    try {
      const res = await claimLaunchOffer(form);
      setResult(res); setStep("result"); toTop();
      if (res.existing) toast.warning("This mobile number is already registered.");
      else toast.success("You're in! 50% launch discount reserved.");
    } catch (err) {
      const d = errDetail(err);
      toast.error(typeof d === "string" ? d : "Could not submit. Please check your details.");
    } finally { setLoading(false); }
  };

  const onStart = async (form) => {
    if (!validate(form)) return;
    setLoading(true);
    try {
      const data = await startAssessment(form);
      setAttempt(data); setStep("quiz"); toTop();
    } catch (err) {
      const d = errDetail(err);
      if (err?.response?.status === 409 && d?.result) { toast.warning(d.message); showExisting(d.result); }
      else toast.error(typeof d === "string" ? d : "Could not start the assessment. Please check your details.");
    } finally { setLoading(false); }
  };

  const onSubmit = async (answers) => {
    setLoading(true);
    try {
      const res = await submitAssessment({ attempt_id: attempt.attempt_id, answers });
      setResult(res); setPrevious(false); setStep("result"); toTop();
    } catch (err) {
      const d = errDetail(err);
      if (err?.response?.status === 409 && d?.result) showExisting(d.result);
      else toast.error("Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="discount-assessment-page" data-mode={meta.mode}>
      <PageHero
        eyebrow={launch ? "Website Launch Offer" : "Discount Assessment"}
        title={launch ? "Claim your 50% launch discount." : "Calculate your course discount."}
        desc={launch ? LAUNCH_MESSAGE : `Answer ${meta.total_questions} simple questions across aptitude, reasoning, maths, English, computer basics, current affairs & etiquette. Score high, save up to 30%.`}
        image={HERO_IMG}
        alt="Student at TASSEI"
      />

      <section className="pt-14 pb-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8" data-testid={`assessment-step-${step}`}>
            {step === "details" && (
              <DetailsForm
                educationLevels={meta.education_levels}
                onStart={launch ? onClaim : onStart}
                onMobileBlur={onMobileBlur}
                loading={loading}
                submitLabel={launch ? "Claim My 50% Discount" : "Start Assessment"}
                subtitle={launch ? "Share your details and we'll reserve your 50% launch discount instantly." : undefined}
              />
            )}
            {step === "quiz" && attempt && <QuizRunner questions={attempt.questions} onSubmit={onSubmit} loading={loading} />}
            {step === "result" && result && (launch ? <LaunchResultCard result={result} /> : <ResultCard result={result} previous={previous} />)}
          </div>

          <aside className="lg:col-span-4 space-y-4">
            {launch ? (
              <>
                <div className="rounded-3xl bg-[#0A192F] text-white p-6" data-testid="launch-offer-card">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FFD66B]"><PartyPopper className="w-4 h-4" /> Launch celebration</div>
                  <div className="mt-3 font-heading text-5xl font-black bg-gradient-to-r from-[#FFB800] via-[#FF7A00] to-[#FF3D71] bg-clip-text text-transparent">50% OFF</div>
                  <p className="mt-2 text-sm text-white/80 leading-relaxed">{LAUNCH_MESSAGE}</p>
                </div>
                <InfoCard Icon={Users} title="First 100 students only" desc="Seats are filling up soon. Register now to lock your discount." />
                <InfoCard Icon={Flame} title="No assessment required" desc="Just share your details — no questions, no test." />
                <InfoCard Icon={ShieldCheck} title="One claim per mobile" desc="Each mobile number can claim the launch offer once." />
              </>
            ) : (
              <>
                <div className="rounded-3xl bg-[#0A192F] text-white p-6">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#7CF0BD]"><Percent className="w-4 h-4" /> Discount slabs</div>
                  <table className="mt-4 w-full text-sm" data-testid="discount-slab-table">
                    <tbody>
                      {TIERS.map(([range, pct]) => (
                        <tr key={range} className="border-t border-white/10">
                          <td className="py-2 text-white/75">{range} correct</td>
                          <td className="py-2 text-right font-heading font-extrabold text-[#7CF0BD]">{pct}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <InfoCard Icon={BrainCircuit} title="15 random questions" desc="Drawn fresh from a bank of 40+ questions every time." />
                <InfoCard Icon={Clock3} title="Valid for 30 days" desc="Your discount & Assessment ID stay valid for 30 days." />
                <InfoCard Icon={ShieldCheck} title="One attempt per mobile" desc="A new attempt is allowed once the previous discount expires." />
              </>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}
